import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { PERIOD_STATUS_LABELS, can, type PeriodStatus } from "@devengo/shared";

import { Dialog } from "../components/period-picker";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatCents, periodName } from "../lib/format";
import { usePeriods, type PeriodOverview } from "../lib/hooks";
import { periodParam, useCurrentUser } from "../lib/session";

const TONE: Record<PeriodStatus, "success" | "brand" | "warning" | "neutral"> = {
  abierto: "success",
  en_revision: "brand",
  cerrado: "warning",
  presentado: "neutral",
};

const ACTIONS: Record<PeriodStatus, { to: PeriodStatus; label: string }[]> = {
  abierto: [
    { to: "en_revision", label: "Pasar a revisión" },
    { to: "cerrado", label: "Cerrar" },
  ],
  en_revision: [
    { to: "abierto", label: "Volver a abrir" },
    { to: "cerrado", label: "Cerrar" },
  ],
  cerrado: [
    { to: "presentado", label: "Marcar presentado" },
    { to: "abierto", label: "Reabrir" },
  ],
  presentado: [],
};

export function PeriodsPage({ companyId }: { companyId: string }) {
  const user = useCurrentUser();
  const periods = usePeriods(companyId);
  const client = useQueryClient();
  const [pending, setPending] = useState<{ period: PeriodOverview; to: PeriodStatus } | null>(null);
  const [reason, setReason] = useState("");
  const transition = useMutation({
    mutationFn: (input: { period: PeriodOverview; to: PeriodStatus; reason?: string }) =>
      api.post(
        `/api/companies/${companyId}/periods/${input.period.year}/${input.period.month}/transition`,
        {
          to: input.to,
          ...(input.reason ? { reason: input.reason } : {}),
        },
      ),
    onSuccess: async () => {
      setPending(null);
      setReason("");
      await client.invalidateQueries({ queryKey: ["periods", companyId] });
      await client.invalidateQueries({ queryKey: ["caja", companyId] });
    },
  });
  const canTransition = can(user.role, "changePeriodStatus");

  return (
    <>
      <PageTitle subtitle="Abierto → en revisión → cerrado → presentado. Cerrar exige la Caja IVA guardada con los comprobantes actuales.">
        Períodos
      </PageTitle>
      <ErrorBox error={transition.error} />
      <Card>
        {periods.data?.length === 0 ? (
          <p className="text-sm text-brand-muted">
            Los períodos aparecen solos con el primer comprobante de cada mes.
          </p>
        ) : (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Período</th>
                <th className={thClass}>Estado</th>
                <th className={`${thClass} text-right`}>Compras</th>
                <th className={`${thClass} text-right`}>Ventas</th>
                <th className={`${thClass} text-right`}>Caja IVA guardada</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {[...(periods.data ?? [])].reverse().map((p) => (
                <tr key={p.id}>
                  <td className={tdClass}>
                    <Link
                      to="/empresas/$companyId/comprobantes"
                      params={{ companyId }}
                      search={{ periodo: periodParam(p.year, p.month) }}
                      className="font-medium text-brand-primary hover:underline"
                    >
                      {periodName(p.year, p.month)}
                    </Link>
                  </td>
                  <td className={tdClass}>
                    <Badge tone={TONE[p.status]}>{PERIOD_STATUS_LABELS[p.status]}</Badge>
                  </td>
                  <td className={`${tdClass} ${numClass}`}>{p.compras}</td>
                  <td className={`${tdClass} ${numClass}`}>{p.ventas}</td>
                  <td className={`${tdClass} ${numClass}`}>
                    {p.latestSnapshot ? (
                      <Link
                        to="/empresas/$companyId/caja"
                        params={{ companyId }}
                        search={{ periodo: periodParam(p.year, p.month) }}
                        className="hover:underline"
                      >
                        v{p.latestSnapshot.version} · a pagar{" "}
                        {formatCents(p.latestSnapshot.saldoAPagarCents)}
                        {p.latestSnapshot.current ? null : (
                          <span className="ml-1 text-xs text-amber-700">
                            (desactualizada: recalculala)
                          </span>
                        )}
                      </Link>
                    ) : (
                      <span className="text-brand-muted">—</span>
                    )}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap text-right`}>
                    {canTransition
                      ? ACTIONS[p.status]
                          .filter(
                            (a) =>
                              !(
                                a.to === "abierto" &&
                                p.status === "cerrado" &&
                                !can(user.role, "reopenPeriod")
                              ),
                          )
                          .map((a) => (
                            <Button
                              key={a.to}
                              variant={
                                a.to === "abierto" && p.status === "cerrado"
                                  ? "danger"
                                  : "secondary"
                              }
                              className="ml-1"
                              disabled={transition.isPending}
                              onClick={() =>
                                a.to === "abierto" && p.status === "cerrado"
                                  ? setPending({ period: p, to: a.to })
                                  : transition.mutate({ period: p, to: a.to })
                              }
                            >
                              {a.label}
                            </Button>
                          ))
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {pending ? (
        <Dialog
          title={`Reabrir ${periodName(pending.period.year, pending.period.month)}`}
          onClose={() => setPending(null)}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              Reabrir un período cerrado queda como evento crítico en la auditoría.
            </p>
            <Field label="Motivo (obligatorio)">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <ErrorBox error={transition.error} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPending(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={() => transition.mutate({ ...pending, reason })}>
                Reabrir
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
